import React from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowLeft, ArrowRight } from "lucide-react";
import { NEWS } from "../mock/mock";

const Article = () => {
  const { id } = useParams();
  const article = NEWS.find((n) => n.id === id);

  if (!article || article.comingSoon) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-32 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-[#15324f] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Article introuvable
        </h1>
        <p className="text-slate-600 mb-8">Cet article n'est pas disponible pour le moment.</p>
        <Link to="/nouvelles" className="inline-flex items-center gap-2 text-[#2f80c4] font-semibold uppercase text-sm tracking-wide">
          <ArrowLeft size={16} /> Retour aux nouvelles
        </Link>
      </div>
    );
  }

  const related = NEWS.filter((n) => !n.comingSoon && n.id !== article.id).slice(0, 2);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${article.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/95 via-[#15324f]/70 to-[#0f2740]/50" />
        <div className="relative h-full max-w-4xl mx-auto px-4 md:px-6 flex flex-col justify-end pb-12">
          <span className="self-start text-[11px] font-bold uppercase tracking-wide text-white bg-[#2f80c4] px-3 py-1 rounded mb-4">
            {article.category}
          </span>
          <h1 className="text-white text-2xl md:text-4xl font-bold leading-tight max-w-3xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 mt-5 text-white/80 text-sm">
            {article.author && (
              <span className="flex items-center gap-2"><User size={15} className="text-[#9bc3e6]" /> {article.author}</span>
            )}
            <span className="flex items-center gap-2"><Calendar size={15} className="text-[#9bc3e6]" /> {article.date}</span>
            {article.readTime && (
              <span className="flex items-center gap-2"><Clock size={15} className="text-[#9bc3e6]" /> {article.readTime} de lecture</span>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Link to="/nouvelles" className="inline-flex items-center gap-2 text-[#2f80c4] text-[13px] font-semibold uppercase tracking-wide mb-10 hover:gap-3 transition-all">
            <ArrowLeft size={15} /> Toutes les nouvelles
          </Link>

          {article.intro && (
            <p className="text-lg md:text-xl text-[#15324f] font-medium leading-relaxed mb-10 pb-10 border-b border-slate-100">
              {article.intro}
            </p>
          )}

          {article.sections?.map((sec) => (
            <div key={sec.heading} className="mb-9">
              <h2 className="text-xl md:text-2xl font-bold text-[#15324f] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {sec.heading}
              </h2>
              {sec.paragraphs?.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
              ))}
              {sec.list && (
                <ul className="space-y-2.5 mt-3">
                  {sec.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-600">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#2f80c4] shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {article.conclusion && (
            <div className="mt-10 p-7 bg-[#f4f6f9] rounded-lg border-l-4 border-[#2f80c4]">
              <p className="text-slate-700 leading-relaxed italic">{article.conclusion}</p>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 text-center">
            <Link to="/nous-joindre" className="inline-flex items-center gap-2 bg-[#15324f] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wide rounded hover:bg-[#2f80c4] transition-colors duration-300" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Discuter avec un expert <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#f4f6f9] py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h3 className="text-xl font-bold text-[#15324f] mb-8 uppercase tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              À lire également
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {related.map((r) => (
                <Link key={r.id} to={`/nouvelles/${r.id}`} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 flex">
                  <div className="w-32 shrink-0 overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#2f80c4]">{r.category}</span>
                    <h4 className="text-sm font-bold text-[#15324f] leading-snug mt-1 group-hover:text-[#2f80c4] transition-colors" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {r.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Article;
